import type {
  DocBlock,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import {
  countWords,
  lowerFirstWord,
  splitLines,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * A limitations paragraph assembled from limitation types the writer picks.
 *
 * For each type the tool supplies two things: a noun phrase ("the size of the sample")
 * that the lead-in sentence is built around, and one general sentence about what that
 * kind of limitation can do to a study's conclusions. Everything specific — the detail,
 * what the writer did about it, and what it means for the findings — is the writer's own
 * sentence, placed as written. The general sentences are accurate for the type but
 * cannot know the study, so the page says to edit them to fit.
 */

export type LimitationScope = "quantitative" | "qualitative" | "all";
export type LimitationKind = "quant" | "qual" | "both";

export const LIMITATION_TYPES = [
  { id: "sampleSize", kind: "quant" },
  { id: "selection", kind: "quant" },
  { id: "observational", kind: "quant" },
  { id: "noComparison", kind: "quant" },
  { id: "measurement", kind: "quant" },
  { id: "attrition", kind: "quant" },
  { id: "confounding", kind: "quant" },
  { id: "analysis", kind: "quant" },
  { id: "transferability", kind: "qual" },
  { id: "researcher", kind: "qual" },
  { id: "sampleQual", kind: "qual" },
  { id: "timeframe", kind: "both" },
  { id: "secondary", kind: "both" },
  { id: "language", kind: "both" },
] as const;

export type LimitationId = (typeof LIMITATION_TYPES)[number]["id"];

export interface LimitationEntry {
  on: boolean;
  /** What exactly: "n = 42", "one university". */
  detail: string;
  /** What the writer did about it, as a sentence. */
  mitigation: string;
  /** What it means for the findings, as a sentence. */
  impact: string;
}

export type LimitationFormat = "paragraph" | "separate" | "bullets";

export interface LimitationsInput {
  scope: LimitationScope;
  entries: Record<LimitationId, LimitationEntry>;
  /** What still holds, finishing "Despite these limitations, …". */
  despite: string;
  format: LimitationFormat;
}

export function emptyEntries(): Record<LimitationId, LimitationEntry> {
  return Object.fromEntries(
    LIMITATION_TYPES.map(({ id }) => [
      id,
      { on: false, detail: "", mitigation: "", impact: "" },
    ])
  ) as Record<LimitationId, LimitationEntry>;
}

/** The types shown for a study type, in the catalogue's order. */
export function typesForScope(scope: LimitationScope): LimitationId[] {
  return LIMITATION_TYPES.filter(
    ({ kind }) =>
      scope === "all" ||
      kind === "both" ||
      (scope === "quantitative" ? kind === "quant" : kind === "qual")
  ).map(({ id }) => id);
}

/** The picked types that are visible under the current scope, in order. */
export function selectedTypes(input: LimitationsInput): LimitationId[] {
  return typesForScope(input.scope).filter((id) => input.entries[id].on);
}

const TERMINAL = /[.!?…]["”’)\]]*$/;
const TRAILING_STOP = /[.\s]+$/;

/** One line, with a full stop if it has no terminal punctuation. */
export function ensureSentence(text: string): string {
  const line = splitLines(text).join(" ").trim();
  if (line === "") {
    return "";
  }
  return TERMINAL.test(line) ? line : `${line}.`;
}

function stripStop(text: string): string {
  return splitLines(text).join(" ").replace(TRAILING_STOP, "");
}

export interface LimitationsLabels {
  defaultTitle: string;
  subtitle: string;
  heading: string;
  tip: string;
  leadIns: readonly string[];
  despiteLead: string;
  phrases: Record<LimitationId, string>;
  consequences: Record<LimitationId, string>;
  prompts: { none: string; despite: string };
}

function leadIn(index: number, labels: LimitationsLabels): string {
  return labels.leadIns[Math.min(index, labels.leadIns.length - 1)];
}

/** The sentences for one limitation, in order. */
export function limitationSentences(
  id: LimitationId,
  entry: LimitationEntry,
  index: number,
  labels: LimitationsLabels
): string[] {
  const detail = stripStop(entry.detail);
  const opening = `${leadIn(index, labels)} ${labels.phrases[id]}${
    detail ? ` (${detail})` : ""
  }.`;
  return [
    opening,
    labels.consequences[id],
    ensureSentence(entry.mitigation),
    ensureSentence(entry.impact),
  ].filter((sentence) => sentence !== "");
}

export function despiteSentence(
  input: LimitationsInput,
  labels: LimitationsLabels,
  locale: string
): string {
  const rest = ensureSentence(input.despite);
  return rest === ""
    ? ""
    : `${labels.despiteLead} ${lowerFirstWord(rest, locale)}`;
}

/** All of the text the tool writes and the writer wrote, as paragraphs of sentences. */
export function limitationParagraphs(
  input: LimitationsInput,
  labels: LimitationsLabels,
  locale: string
): string[][] {
  const groups = selectedTypes(input).map((id, index) =>
    limitationSentences(id, input.entries[id], index, labels)
  );
  const despite = despiteSentence(input, labels, locale);
  if (input.format === "paragraph") {
    const sentences = [...groups.flat(), ...(despite ? [despite] : [])];
    return sentences.length > 0 ? [sentences] : [];
  }
  return [...groups, ...(despite ? [[despite]] : [])];
}

export function limitationsText(
  input: LimitationsInput,
  labels: LimitationsLabels,
  locale: string
): string {
  return limitationParagraphs(input, labels, locale)
    .map((sentences) => sentences.join(" "))
    .join("\n\n");
}

export function limitationsWords(
  input: LimitationsInput,
  labels: LimitationsLabels,
  locale: string
): number {
  return countWords(limitationsText(input, labels, locale));
}

export type LimitationCheckId =
  | "chosen"
  | "mitigation"
  | "impact"
  | "detail"
  | "despite";

export interface LimitationCheck {
  id: LimitationCheckId;
  state: "done" | "todo" | "note";
  /** Which limitation numbers (from 1) the message refers to. */
  items?: number[];
}

function missing(
  input: LimitationsInput,
  field: "mitigation" | "impact" | "detail"
): number[] {
  return selectedTypes(input)
    .map((id, index) =>
      input.entries[id][field].trim() === "" ? index + 1 : 0
    )
    .filter((n) => n > 0);
}

export function limitationChecks(input: LimitationsInput): LimitationCheck[] {
  if (selectedTypes(input).length === 0) {
    return [{ id: "chosen", state: "todo" }];
  }
  const checks: LimitationCheck[] = [{ id: "chosen", state: "done" }];
  for (const [id, field] of [
    ["detail", "detail"],
    ["mitigation", "mitigation"],
    ["impact", "impact"],
  ] as const) {
    const numbers = missing(input, field);
    checks.push(
      numbers.length === 0
        ? { id, state: "done" }
        : { id, state: "note", items: numbers }
    );
  }
  checks.push({
    id: "despite",
    state: input.despite.trim() === "" ? "note" : "done",
  });
  return checks;
}

export function buildLimitationsDocument(
  input: LimitationsInput,
  labels: LimitationsLabels,
  locale: string
): DocumentModel {
  const paragraphs = limitationParagraphs(input, labels, locale);
  let body: DocBlock[];
  if (paragraphs.length === 0) {
    body = [{ kind: "prompt", text: labels.prompts.none }];
  } else if (input.format === "bullets") {
    body = [
      {
        kind: "list",
        list: {
          style: "bullet",
          items: paragraphs.map((sentences) => ({ text: sentences.join(" ") })),
        },
      },
    ];
  } else {
    body = paragraphs.map((sentences) => ({
      kind: "paragraph",
      text: sentences.join(" "),
    }));
  }
  const section: DocSection = {
    heading: labels.heading,
    blocks: [{ kind: "guidance", text: labels.tip }, ...body],
  };
  return {
    title: labels.defaultTitle,
    subtitle: labels.subtitle,
    sections: [section],
  };
}
